import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Input from '../../../components/ui/Input';
import Button from '../../../components/ui/Button';
import { supabase } from '../../../lib/supabase';
import { validateEmail } from '../../../utils/emailValidation';

// To add more roles in the future, add the value here AND update the DB check constraint.
const ROLE_OPTIONS = ['Yard Manager', 'Fleet Manager'];

const FleetContactFormModal = ({ contact, yards, onClose, onSave }) => {
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    email: '',
    role: '',
    yard_id: '',
    selectedYardIds: []
  });
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (contact) {
      // Derive role: prefer the new role column, fall back to contactfleetmgr for legacy rows.
      // If the stored role is not in ROLE_OPTIONS (e.g. legacy 'Contact'), keep it so the
      // admin can see what's stored and must explicitly choose a valid role before saving.
      const storedRole = contact?.role || (contact?.contactfleetmgr ? 'Fleet Manager' : '');

      setFormData({
        name: contact?.name || '',
        phone: contact?.phone || '',
        email: contact?.email || '',
        role: storedRole,
        yard_id: contact?.yard_id || '',
        selectedYardIds: contact?.selectedYardIds || []
      });
    }
  }, [contact]);

  const isFleetManager = formData?.role === 'Fleet Manager';
  const yardOptions = yards?.map(y => ({ value: y?.id, label: y?.name })) || [];

  const validate = () => {
    const newErrors = {};
    if (!formData?.name?.trim()) newErrors.name = 'Name is required';
    if (!formData?.role || !ROLE_OPTIONS?.includes(formData?.role)) {
      newErrors.role = ROLE_OPTIONS?.includes(formData?.role)
        ? null
        : 'Please select a valid role (Yard Manager or Fleet Manager)';
    }
    const emailErr = validateEmail(formData?.email);
    if (emailErr) newErrors.email = emailErr;
    setErrors(newErrors);
    return Object.keys(newErrors)?.length === 0;
  };

  const handleYardMultiToggle = (yardId) => {
    setFormData(prev => {
      const already = prev?.selectedYardIds?.includes(yardId);
      return {
        ...prev,
        selectedYardIds: already
          ? prev?.selectedYardIds?.filter(id => id !== yardId)
          : [...prev?.selectedYardIds, yardId]
      };
    });
  };

  const handleRoleChange = (newRole) => {
    setFormData(prev => ({
      ...prev,
      role: newRole,
      // Reset yard selections when switching roles
      selectedYardIds: [],
      yard_id: ''
    }));
  };

  const handleEmailChange = (e) => {
    const val = e?.target?.value;
    setFormData(prev => ({ ...prev, email: val }));
    const err = validateEmail(val);
    setErrors(prev => ({ ...prev, email: err || undefined }));
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();
    if (!validate()) return;
    setIsSubmitting(true);

    try {
      const isFleetMgr = formData?.role === 'Fleet Manager';

      const payload = {
        name: formData?.name?.trim(),
        phone: formData?.phone?.trim() || null,
        email: formData?.email?.trim() || null,
        role: formData?.role,
        // Keep contactfleetmgr in sync so any other code reading it still works
        contactfleetmgr: isFleetMgr,
        yard_id: isFleetMgr
          ? (formData?.selectedYardIds?.[0] || null)
          : (formData?.yard_id || null)
      };

      let contactId;

      if (contact?.id) {
        const { error } = await supabase?.from('fleet_contacts')?.update(payload)?.eq('id', contact?.id);
        if (error) throw error;
        contactId = contact?.id;
      } else {
        const { data, error } = await supabase?.from('fleet_contacts')?.insert(payload)?.select('id')?.single();
        if (error) throw error;
        contactId = data?.id;
      }

      if (isFleetMgr) {
        await supabase?.from('fleet_contact_yards')?.delete()?.eq('contact_id', contactId);

        if (formData?.selectedYardIds?.length > 0) {
          const rows = formData?.selectedYardIds?.map(yid => ({
            contact_id: contactId,
            yard_id: yid
          }));
          const { error: insertErr } = await supabase?.from('fleet_contact_yards')?.insert(rows);
          if (insertErr) throw insertErr;
        }
      }

      onSave();
    } catch (err) {
      console.error('Error saving fleet contact:', err);
      setErrors({ submit: err?.message || 'Failed to save contact' });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-background/80 backdrop-blur-sm z-[200] flex items-center justify-center p-4">
      <div className="bg-card border border-border rounded-lg shadow-elevation-3 w-full max-w-lg max-h-[90vh] flex flex-col">
        <div className="flex items-center justify-between px-6 py-4 border-b border-border">
          <div className="flex items-center space-x-3">
            <div className="flex items-center justify-center w-10 h-10 bg-primary/10 rounded-lg">
              <Icon name={contact ? 'Edit2' : 'UserPlus'} size={20} color="var(--color-primary)" strokeWidth={2} />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-foreground">
                {contact ? 'Edit Fleet Contact' : 'Add Fleet Contact'}
              </h2>
              <p className="text-xs text-muted-foreground">
                {contact ? 'Update contact information' : 'Enter contact details'}
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 text-muted-foreground hover:text-foreground hover:bg-muted/50 rounded-lg transition-hover"
          >
            <Icon name="X" size={20} strokeWidth={2} />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto px-6 py-4">
          <div className="space-y-5">
            <Input
              label="Full Name"
              type="text"
              placeholder="Jane Smith"
              value={formData?.name}
              onChange={(e) => setFormData(prev => ({ ...prev, name: e?.target?.value }))}
              error={errors?.name}
              required
            />

            <div className="grid grid-cols-2 gap-4">
              <Input
                label="Phone"
                type="tel"
                placeholder="+15551234567"
                value={formData?.phone}
                onChange={(e) => setFormData(prev => ({ ...prev, phone: e?.target?.value }))}
              />
              <div>
                <label className="block text-sm font-medium text-foreground mb-1">Email</label>
                <input
                  type="text"
                  placeholder="jane@company.com"
                  value={formData?.email}
                  onChange={handleEmailChange}
                  className={`w-full h-10 px-3 py-2 text-sm bg-background border rounded-lg text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary ${errors?.email ? 'border-destructive' : 'border-border'}`}
                />
                {errors?.email && (
                  <p className="mt-1 text-xs text-destructive flex items-center gap-1">
                    <Icon name="AlertCircle" size={12} strokeWidth={2} />
                    {errors?.email}
                  </p>
                )}
              </div>
            </div>

            <div className="border-t border-border pt-4">
              <label className="block text-sm font-medium text-foreground mb-2">
                Role <span className="text-destructive">*</span>
              </label>
              <select
                value={formData?.role}
                onChange={(e) => handleRoleChange(e?.target?.value)}
                className={`w-full h-10 px-3 py-2 text-sm bg-background border rounded-lg text-foreground focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary ${errors?.role ? 'border-destructive' : 'border-border'}`}
              >
                <option value="" disabled>Select a role…</option>
                {ROLE_OPTIONS?.map(r => (
                  <option key={r} value={r}>{r}</option>
                ))}
                {/* Show legacy/unrecognized role as a disabled option so the admin can see it */}
                {formData?.role && !ROLE_OPTIONS?.includes(formData?.role) && (
                  <option value={formData?.role} disabled>
                    {formData?.role} (legacy — please select a valid role)
                  </option>
                )}
              </select>
              {errors?.role && (
                <p className="text-xs text-destructive mt-1">{errors?.role}</p>
              )}
            </div>

            <div className="border-t border-border pt-4">
              {isFleetManager ? (
                <div>
                  <label className="block text-sm font-medium text-foreground mb-2">
                    Assigned Yards
                    <span className="text-xs text-muted-foreground ml-2">(select all that apply)</span>
                  </label>
                  {yardOptions?.length === 0 ? (
                    <p className="text-sm text-muted-foreground">No yards available</p>
                  ) : (
                    <div className="space-y-2 max-h-48 overflow-y-auto border border-border rounded-lg p-3">
                      {yardOptions?.map(opt => (
                        <label
                          key={opt?.value}
                          className="flex items-center space-x-3 cursor-pointer hover:bg-muted/30 rounded px-2 py-1.5 transition-hover"
                        >
                          <input
                            type="checkbox"
                            checked={formData?.selectedYardIds?.includes(opt?.value)}
                            onChange={() => handleYardMultiToggle(opt?.value)}
                            className="w-4 h-4 rounded border-border text-primary focus:ring-2 focus:ring-primary/20"
                          />
                          <span className="text-sm text-foreground">{opt?.label}</span>
                        </label>
                      ))}
                    </div>
                  )}
                  {formData?.selectedYardIds?.length > 0 && (
                    <p className="text-xs text-muted-foreground mt-1">
                      {formData?.selectedYardIds?.length} yard{formData?.selectedYardIds?.length !== 1 ? 's' : ''} selected
                    </p>
                  )}
                </div>
              ) : (
                <div>
                  <label className="block text-sm font-medium text-foreground mb-2">Yard Location</label>
                  <select
                    value={formData?.yard_id}
                    onChange={(e) => setFormData(prev => ({ ...prev, yard_id: e?.target?.value }))}
                    className="w-full h-10 px-3 py-2 text-sm bg-background border border-border rounded-lg text-foreground focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary"
                  >
                    <option value="">Select yard...</option>
                    {yardOptions?.map(opt => (
                      <option key={opt?.value} value={opt?.value}>{opt?.label}</option>
                    ))}
                  </select>
                </div>
              )}
            </div>

            {errors?.submit && (
              <div className="flex items-center space-x-2 p-3 bg-destructive/10 border border-destructive/20 rounded-lg">
                <Icon name="AlertCircle" size={16} color="var(--color-destructive)" strokeWidth={2} />
                <p className="text-sm text-destructive">{errors?.submit}</p>
              </div>
            )}
          </div>
        </form>

        <div className="flex items-center justify-end space-x-3 px-6 py-4 border-t border-border bg-muted/30">
          <Button variant="outline" onClick={onClose} disabled={isSubmitting}>
            Cancel
          </Button>
          <Button
            variant="default"
            onClick={handleSubmit}
            loading={isSubmitting}
            iconName="Save"
          >
            {contact ? 'Update Contact' : 'Add Contact'}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default FleetContactFormModal;
